/**
 * 签收证明展示规则匹配工具
 *
 * CMS 配置格式：始发国代码-目的国代码，* 表示不限制，逗号分隔多条规则取并集
 * 示例：*-kr,cn-*
 */

const CONFIG_KEY = 'receipt_proof_flow_rules'
const WEB_FLOW_CONFIG_KEY = 'web_proof_flow_rules'
const SECTION_PROP_MAP = {
  // signature → 签收证明 (Tab 1, type 252)
  signature: 'showReceiptProof',
  // receipt → 派送证明 (Tab 2, type 15)
  receipt: 'showDeliveryCert',
  // deliveryCopy → 签收底单 (Tab 5, types 2,5)
  deliveryCopy: 'showSignReceipt',
  // returnPod → 签单返还 (Tab 3, type 71)
  returnPod: 'showSignedDO',
  // extension → 宅配延伸 (Tab 4, type 68)
  extension: 'showPostAssembly',
}

/**
 * 从 CMS 获取流向规则配置（每次调用都发起请求，不使用缓存）
 * @param {Function} getSysConfig - API 方法
 * @returns {Promise<string|null>} 配置值
 */
export async function fetchFlowRules(getSysConfig) {
  try {
    const configValue = await getSysConfig(CONFIG_KEY)
    return configValue || ''
  } catch (e) {
    console.error('[ReceiptProof] 获取 CMS 配置失败:', e)
    return ''
  }
}

/**
 * 匹配单条规则
 * @param {string} pattern - 模式（如 *, CN, KR）
 * @param {string} value - 实际值
 * @returns {boolean}
 */
function matchPart(pattern, value) {
  const trimmedPattern = pattern.trim()
  const trimmedValue = (value || '').trim()

  // 支持 ! 前缀表示"不等于"
  if (trimmedPattern.startsWith('!')) {
    return trimmedPattern.slice(1).toLowerCase() !== trimmedValue.toLowerCase()
  }

  return trimmedPattern === '*' ||
         trimmedPattern.toLowerCase() === trimmedValue.toLowerCase()
}

/**
 * 检查运单是否符合签收证明展示条件
 * @param {string} flowRules - CMS 配置值（如 *-kr,cn-*）
 * @param {string} sourceCountryCode - 始发国代码
 * @param {string} destCountryCode - 目的国代码
 * @returns {boolean}
 */
export function matchFlowRule(flowRules, sourceCountryCode, destCountryCode) {
  if (!flowRules || !sourceCountryCode || !destCountryCode) {
    return false
  }

  const rules = flowRules.split(',').map(r => r.trim()).filter(r => r.includes('-'))

  return rules.some(rule => {
    const [originPattern, destPattern] = rule.split('-', 2)
    return matchPart(originPattern, sourceCountryCode) &&
           matchPart(destPattern, destCountryCode)
  })
}

/**
 * 解析 CMS 配置（兼容新旧格式）
 * 新格式：JSON 对象 { "C921": { "flow": "*_*", "businessCode": "3" }, "Other": { "flow": "CN_*", "businessCode": "4" } }
 * 旧格式：字符串 "*-kr,cn-*" → 自动降级为 { Other: { flow: raw, businessCode: "4" } }
 * @param {string|object} raw - CMS 原始配置值
 * @returns {object} 标准化后的配置对象
 */
export function parseProductFlowConfig(raw) {
  if (!raw) return {}
  if (typeof raw === 'object') return raw
  try {
    const parsed = JSON.parse(raw)
    if (typeof parsed === 'object' && parsed !== null) return parsed
  } catch (e) {
    console.warn('[ReceiptProof] CMS config JSON parse failed, fallback to legacy format:', e.message)
    // JSON 解析失败，走旧格式兼容
  }
  // 旧格式兼容：字符串 → Other 兜底条目
  return { Other: { flow: raw, businessCode: '4' } }
}

/**
 * 获取并解析 CMS 配置（兼容新旧格式）
 * @param {Function} getSysConfig - API 方法
 * @returns {Promise<object>} 标准化后的配置对象
 */
export async function fetchProductFlowConfig(getSysConfig) {
  const raw = await fetchFlowRules(getSysConfig)
  return parseProductFlowConfig(raw)
}

/**
 * 根据产品类型和流向匹配 CMS 配置
 * 匹配优先级：cargoTypeCode 精确匹配 → Other 兜底
 * @param {object} config - parseProductFlowConfig 解析后的配置对象
 * @param {string} cargoTypeCode - 产品类型代码
 * @param {string} sourceCountryCode - 始发国代码
 * @param {string} destCountryCode - 目的国代码
 * @returns {{ eligible: boolean, businessCode: string }}
 */
export function matchProductFlowRule(config, cargoTypeCode, sourceCountryCode, destCountryCode) {
  const entry = config[cargoTypeCode] || config.Other
  if (!entry || !entry.flow || typeof entry.flow !== 'string') {
    return { eligible: false, businessCode: '4' }
  }
  const eligible = matchFlowRule(entry.flow, sourceCountryCode, destCountryCode)
  return { eligible, businessCode: entry.businessCode || '4' }
}

/**
 * 获取并解析 Web 签收证明配置（5个区块）
 * 配置格式：section → cargoTypeCode → { flow, businessCode }
 * {
 *   "signature": {
 *     "C919": { "flow": "*-*", "businessCode": "3" },
 *     "C921": { "flow": "*-sg,*-my", "businessCode": "5" },
 *     "Other": { "flow": "*-cn,*-us,my-my,sg-sg", "businessCode": "4" }
 *   },
 *   "receipt": { ... },
 *   "deliveryCopy": { ... },
 *   "returnPod": { ... },
 *   "extension": { ... }
 * }
 *
 * section 对应关系：
 *   signature    → 签收证明 (Tab 1, type 252)
 *   receipt      → 派送证明 (Tab 2, type 15)
 *   deliveryCopy → 签收底单 (Tab 5, types 2,5)
 *   returnPod    → 签单返还 (Tab 3, type 71)
 *   extension    → 宅配延伸 (Tab 4, type 68)
 *
 * @param {Function} getSysConfig - API 方法
 * @returns {Promise<object>} 配置对象
 */
export async function fetchWebFlowConfig(getSysConfig) {
  try {
    const configValue = await getSysConfig(WEB_FLOW_CONFIG_KEY)
    return parseWebFlowConfig(configValue)
  } catch (e) {
    console.error('[ReceiptProof] 获取 Web CMS 配置失败:', e)
    return {}
  }
}

/**
 * 解析 Web 签收证明配置
 * @param {string|object} raw - CMS 原始配置值
 * @returns {object} 配置对象
 */
export function parseWebFlowConfig(raw) {
  if (!raw) return {}
  if (typeof raw === 'object') return raw
  try {
    const parsed = JSON.parse(raw)
    if (typeof parsed === 'object' && parsed !== null) return parsed
  } catch (e) {
    console.warn('[ReceiptProof] Web CMS config JSON parse failed:', e.message)
  }
  return {}
}

/**
 * 匹配单个区块的 Web 配置
 * 查找顺序：config[section][cargoTypeCode] → config[section]["Other"]
 * @param {object} config - parseWebFlowConfig 解析后的配置对象
 * @param {string} section - 区块名称（signature/receipt/deliveryCopy/returnPod/extension）
 * @param {string} cargoTypeCode - 产品类型代码
 * @param {string} sourceCountryCode - 始发国代码
 * @param {string} destCountryCode - 目的国代码
 * @returns {{ eligible: boolean, businessCode: string }}
 */
export function matchWebFlowRule(config, section, cargoTypeCode, sourceCountryCode, destCountryCode) {
  const sectionConfig = config[section]
  if (!sectionConfig) {
    return { eligible: false, businessCode: '4' }
  }
  const entry = sectionConfig[cargoTypeCode] || sectionConfig.Other
  if (!entry || !entry.flow || typeof entry.flow !== 'string') {
    return { eligible: false, businessCode: '4' }
  }
  const eligible = matchFlowRule(entry.flow, sourceCountryCode, destCountryCode)
  return { eligible, businessCode: entry.businessCode || '4' }
}

/**
 * 匹配所有 5 个区块的 Web 配置
 * @param {object} config - parseWebFlowConfig 解析后的配置对象
 * @param {string} cargoTypeCode - 产品类型代码
 * @param {string} sourceCountryCode - 始发国代码
 * @param {string} destCountryCode - 目的国代码
 * @returns {{ showReceiptProof: boolean, showDeliveryCert: boolean, showSignReceipt: boolean, showSignedDO: boolean, showPostAssembly: boolean, businessCode: string }}
 */
export function matchAllWebFlowRules(config, cargoTypeCode, sourceCountryCode, destCountryCode) {
  const result = {
    showReceiptProof: false,
    showDeliveryCert: false,
    showSignReceipt: false,
    showSignedDO: false,
    showPostAssembly: false,
    businessCode: '4',
  }

  let businessCodeSet = false

  Object.keys(SECTION_PROP_MAP).forEach(section => {
    const match = matchWebFlowRule(config, section, cargoTypeCode, sourceCountryCode, destCountryCode)
    const propName = SECTION_PROP_MAP[section]
    result[propName] = match.eligible
    if (match.eligible && !businessCodeSet) {
      result.businessCode = match.businessCode
      businessCodeSet = true
    }
  })

  return result
}